import { useEffect } from "react";
import { useProductData } from "@/zustand/productStore.js";
import { useTemplateStore } from "@/zustand/templateStore.js";
import { useMarketingStore } from "@/zustand/emailMarketingStore.js";
import getCookie from "@/func/getCookie.js";
import Overlay from "@/components/Misc/Overlay.jsx";
import Header from "@/components/Header/Header";
import { Form } from "@/components/Forms/Form.jsx";
import BenefitsList from "@/components/Benefits/BenefitsList.jsx";
import TestimonialsList from "@/components/Testimonials/TestimonialsList";
import Footer from "@/components/Footer/Footer.jsx";
import MobileSectionSeperator from "@/components/MobileComponents/MobileSectionSeperator";
import UpsellPopupOverlay from "@/components/Popups/upsellPopupOverlay.jsx";
import ProductPackages from "@/components/ProductPackages/ProductPackages";
import OrderSummary from "@/components/OrderSummary/OrderSummary.jsx";
import FreeShippingPopup from "@/components/Popups/FreeShippingPopup.jsx";
import UpsellPopup from "@/components/Popups/UpsellPopup.jsx";
import { useInitializeDefaultProduct } from "@/func/useInitializeDefaultProduct.js";
import PromoTimerBanner from "@/components/Misc/PromoTimerBanner";
import VideoImageContainer from "@/components/Misc/VideoImageContainer";
import OverViewDrawerImageMobileFallback from "@/assets/images/overviewDrawerImageMobile.jpg";
import s from "@/layouts/Layout.module.scss";

const Layout = () => {
  useInitializeDefaultProduct();
  const {
    isLoading,
    paymentMethod,
    upsellPopup,
    setLoadingState,
    withProductPicker,
    complianceDesktop,
    complianceMobile,
    isMobile,
    setIsMobile,
    setIsMobileGallery,
  } = useTemplateStore();
  const { freeShippingApplied, freeShippingEnabled } = useMarketingStore();
  const productData = useProductData();

  const base = window.post_template;
  const orderSummaryMediaType = base?.order_overview_media_type;
  const orderSummaryImage = base?.order_overview_image;
  const orderSummaryVideo = base?.order_overview_r2_bucket_video_url;
  const complianceMode = (isMobile == false && complianceDesktop) || (isMobile == true && complianceMobile);
  const orderSummaryVersion = base?.checkout_v6_summary?.checkout_v6_summary_version;

  useEffect(() => {
    const encodedAffParam = new URL(location).searchParams.get("aff");
    const decodedAffParam = atob(encodedAffParam);
    const affCookie = getCookie("affiliate_tag");
    const affiliateTag = encodedAffParam ? decodedAffParam : affCookie;

    if (window?.postId) {
      productData.get({
        postId: window.postId,
        isMobile: isMobile,
        affParam: affiliateTag,
      });
    }
  }, [isMobile]);

  useEffect(() => {
    function updateIsMobile() {
      setIsMobile(window.innerWidth <= 767);
      setIsMobileGallery(window.innerWidth < 1072);
    }

    window.addEventListener("resize", updateIsMobile);
    updateIsMobile();
    return () => {
      window.removeEventListener("resize", updateIsMobile);
    };
  }, []);

  useEffect(() => {
    const handlePageHide = () => {
      setLoadingState(false);
    };

    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, []);

  return (
    <>
      <Header isMobile={isMobile} />
      <main className={s.main} data-cy="cc-v4">
        <div className={s.main__leftSide}></div>
        <section className={s.contentContainer}>
          {isMobile && (
            <VideoImageContainer
              mediaType={orderSummaryMediaType}
              videoUrl={orderSummaryVideo}
              image={orderSummaryImage || OverViewDrawerImageMobileFallback}
            />
          )}
          <article className={s.productContainer}>
            {orderSummaryVersion && isMobile && (
              <div style={{ marginBottom: "16px" }}>
                <OrderSummary isMobile={isMobile} />
              </div>
            )}
            <PromoTimerBanner />
            {withProductPicker && <ProductPackages />}
            <MobileSectionSeperator margin="0px 0px 24px -16px" />
            <Form />
            {!isMobile && <Footer />}
          </article>
          <div className={`${s.summaryContainer} ${s.v2}`}>
            {!(isMobile && orderSummaryVersion) && <OrderSummary isMobile={isMobile} />}
            <BenefitsList />
            {!complianceMode && <TestimonialsList />}
          </div>
          {isMobile && <Footer />}
        </section>
        <div className={s.main__rightSide}></div>
      </main>

      {((upsellPopup?.popupClicked && !upsellPopup?.popupClicked) || isLoading) && (
        <Overlay loading={isLoading} paymentMethod={paymentMethod} />
      )}
      {upsellPopup?.displayPopup && upsellPopup?.popupEnabled && (
        <UpsellPopupOverlay>
          <UpsellPopup />
        </UpsellPopupOverlay>
      )}
      {freeShippingApplied && freeShippingEnabled && <FreeShippingPopup />}
    </>
  );
};

export { Layout };
